/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect, useMemo } from 'react';
import { timeApi, projectsApi, tasksApi, adminApi, teamsApi, absencesApi, googleCalendarApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { 
  Plus, Clock, CalendarBlank, Trash, CheckCircle, Info, Target,
  ArrowLeft, ArrowRight, Users, User, Eye, PencilSimple, Download, FileXls, FilePdf, CalendarX
} from '@phosphor-icons/react';
import { 
  format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay,
  addWeeks, subWeeks, startOfMonth, endOfMonth, addMonths, subMonths,
  isWithinInterval, eachWeekOfInterval, startOfDay, endOfDay
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import { formatTimeHundredths, getInitials, toNoonUTC } from '../lib/utils';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ACTIVITIES = [
  'Developpement', 'Tests', 'Analyse', 'Conception',
  'Reunion', 'Documentation', 'Support', 'Formation', 'Autre',
];

export default function TimeTrackingPage() {
  const { user, isManager, hasPermission } = useAuth();
  const [timeEntries, setTimeEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [viewPeriod, setViewPeriod] = useState('week'); // week | month
  const [viewScope, setViewScope] = useState('my'); // my | team
  const [scopeInitialized, setScopeInitialized] = useState(false);
  const [resourceFilter, setResourceFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [taskFilter, setTaskFilter] = useState('all');
  const [googleStatus, setGoogleStatus] = useState({ configured: false, linked: false });
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [craMonthStatus, setCraMonthStatus] = useState(null);
  const [newEntry, setNewEntry] = useState({
    project_id: '', task_id: '', activity: '',
    date: new Date(), time_spent: '', description: '', billable: true,
  });

  // Edit state
  const [editingEntry, setEditingEntry] = useState(null);

  // Absences state
  const [absences, setAbsences] = useState([]);
  const [projectsProgress, setProjectsProgress] = useState([]);
  const [absenceDialog, setAbsenceDialog] = useState({ open: false, editing: null });
  const [absenceForm, setAbsenceForm] = useState({ user_id: '', start_date: new Date(), end_date: new Date(), type: 'conges', reason: '', status: 'pending' });
  const ABSENCE_TYPES = [
    { value: 'conges', label: 'Conges payes' }, { value: 'maladie', label: 'Maladie' },
    { value: 'rtt', label: 'RTT' }, { value: 'formation', label: 'Formation' },
    { value: 'teletravail', label: 'Teletravail' }, { value: 'autre', label: 'Autre' },
  ];

  // Period calculations
  const periodStart = viewPeriod === 'week'
    ? startOfWeek(selectedDate, { weekStartsOn: 1 })
    : startOfMonth(selectedDate);
  const periodEnd = viewPeriod === 'week'
    ? endOfWeek(selectedDate, { weekStartsOn: 1 })
    : endOfMonth(selectedDate);
  const periodDays = eachDayOfInterval({ start: periodStart, end: periodEnd });

  useEffect(() => { fetchData(); }, [viewScope]);
  useEffect(() => {
    googleCalendarApi.status().then(r => setGoogleStatus(r.data || { configured:false, linked:false })).catch(() => {});
  }, []);

  // Default to team view for managers/admins (only on first load)
  useEffect(() => {
    if (!scopeInitialized && isManager) {
      setViewScope('team');
    }
    setScopeInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager]);

  const fetchData = async () => {
    try {
      const promises = [
        viewScope === 'team' && isManager ? timeApi.getTeam() : timeApi.getMy(),
        projectsApi.getAll(),
        tasksApi.getAll(),
      ];
      if (isManager) {
        promises.push(adminApi.getVisibleUsers().catch(() => ({ data: [] })));
        promises.push(teamsApi.getAll().catch(() => ({ data: [] })));
      }
      
      const results = await Promise.all(promises);
      setTimeEntries(results[0].data);
      setProjects(results[1].data);
      setTasks(results[2].data);
      if (results[3]) setUsers(results[3].data);
      if (results[4]) setTeams(results[4].data);
      // Fetch absences + projects progress
      try { const absRes = await absencesApi.getAll(); setAbsences(absRes.data || []); } catch {}
      try {
        const ppRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bi/projects-progress`, { credentials: 'include' });
        if (ppRes.ok) setProjectsProgress(await ppRes.json());
      } catch {}
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadMyCraStatus = async () => {
    if (!user?.is_external_provider) return;
    const month = format(new Date(), 'yyyy-MM');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/time/cra-monthly/my-status?month=${month}`, { credentials:'include' });
      if (r.ok) setCraMonthStatus(await r.json());
    } catch {}
  };

  useEffect(() => { loadMyCraStatus(); /* eslint-disable-next-line */ }, [user?.id]);

  const submitMyCra = async () => {
    const month = format(new Date(), 'yyyy-MM');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/time/cra-monthly/submit`, { method:'POST', credentials:'include', headers:{'Content-Type':'application/json'}, body:JSON.stringify({month}) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Erreur');
      toast.success('CRA valide et soumis au responsable');
      await loadMyCraStatus();
    } catch(e) { toast.error(e.message || 'Erreur CRA'); }
  };

  const syncGoogleCalendar = async () => {
    if (!googleStatus.linked) {
      toast.info('Rattachez d abord votre adresse Google dans Mon profil > Google Agenda.');
      return;
    }
    setGoogleSyncing(true);
    try {
      const start = startOfDay(periodStart).toISOString();
      const end = endOfDay(periodEnd).toISOString();
      const r = await googleCalendarApi.sync({ start, end });
      const d = r.data || {};
      toast.success(`Google Agenda : ${d.imported || 0} ajout(s), ${d.updated || 0} mise(s) a jour${d.conflicts ? `, ${d.conflicts} conflit(s)` : ''}`);
      await fetchData();
      const st = await googleCalendarApi.status().catch(() => null);
      if (st?.data) setGoogleStatus(st.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de synchronisation Google Agenda'); }
    finally { setGoogleSyncing(false); }
  };

  const handleCreateEntry = async () => {
    try {
      await timeApi.create({
        ...newEntry,
        date: toNoonUTC(newEntry.date),
        time_spent: parseFloat(newEntry.time_spent),
        task_id: newEntry.task_id && newEntry.task_id !== 'none' ? newEntry.task_id : null,
        billable: newEntry.billable,
      });
      toast.success('Temps enregistre');
      setIsCreateDialogOpen(false);
      setNewEntry({ project_id: '', task_id: '', activity: '', date: new Date(), time_spent: '', description: '', billable: true });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erreur';
      if (detail.includes('journali')) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail);
      }
    }
  };

  const handleDeleteEntry = async (id) => {
    try {
      await timeApi.delete(id);
      toast.success('Entree supprimee');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleValidateEntry = async (id) => {
    try {
      await timeApi.validate(id);
      toast.success('Entree validee');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUpdateEntry = async () => {
    if (!editingEntry) return;
    try {
      await timeApi.update(editingEntry.id, {
        time_spent: parseFloat(editingEntry.time_spent),
        description: editingEntry.description,
        activity: editingEntry.activity,
        billable: editingEntry.billable,
      });
      toast.success('Entree modifiee');
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Navigate period
  const navigatePeriod = (dir) => {
    if (viewPeriod === 'week') {
      setSelectedDate(dir === 'next' ? addWeeks(selectedDate, 1) : subWeeks(selectedDate, 1));
    } else {
      setSelectedDate(dir === 'next' ? addMonths(selectedDate, 1) : subMonths(selectedDate, 1));
    }
  };

  // Filtered entries for current period
  const periodEntries = useMemo(() => {
    let entries = timeEntries.filter(e => {
      const d = new Date(e.date);
      // Compare date-only (ignore time/timezone)
      const eDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      return isWithinInterval(eDate, { start: startOfDay(periodStart), end: endOfDay(periodEnd) });
    });
    if (resourceFilter !== 'all') entries = entries.filter(e => e.user_id === resourceFilter);
    if (projectFilter !== 'all') entries = entries.filter(e => e.project_id === projectFilter);
    if (taskFilter !== 'all') entries = entries.filter(e => e.task_id === taskFilter);
    return entries;
  }, [timeEntries, periodStart, periodEnd, resourceFilter, projectFilter, taskFilter]);

  // Daily totals
  const getDayTotal = (date, userId) => {
    return timeEntries
      .filter(e => {
        const d = new Date(e.date);
        const eDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
        return isSameDay(eDate, date) && (!userId || e.user_id === userId);
      })
      .reduce((sum, e) => sum + e.time_spent, 0);
  };

  // Period total
  const periodTotal = useMemo(() => 
    periodEntries.reduce((sum, e) => sum + e.time_spent, 0),
    [periodEntries]
  );

  // Group by user (for team view)
  const entriesByUser = useMemo(() => {
    if (viewScope !== 'team') return {};
    const grouped = {};
    periodEntries.forEach(e => {
      const uid = e.user_id;
      if (!grouped[uid]) grouped[uid] = { name: e.user_name || 'Inconnu', entries: [] };
      grouped[uid].entries.push(e);
    });
    return grouped;
  }, [periodEntries, viewScope]);

  // Entries for selected date
  const selectedDateEntries = useMemo(() => {
    let entries = timeEntries.filter(e => isSameDay(new Date(e.date), selectedDate));
    if (resourceFilter !== 'all') entries = entries.filter(e => e.user_id === resourceFilter);
    if (projectFilter !== 'all') entries = entries.filter(e => e.project_id === projectFilter);
    if (taskFilter !== 'all') entries = entries.filter(e => e.task_id === taskFilter);
    return entries;
  }, [timeEntries, selectedDate, resourceFilter, projectFilter, taskFilter]);

  // Absence functions
  const openAbsenceDialog = (abs = null) => {
    if (abs) {
      setAbsenceForm({ user_id: abs.user_id || user.id, start_date: new Date(abs.start_date), end_date: new Date(abs.end_date), type: abs.type || 'conges', reason: abs.reason || '', status: abs.status || 'pending' });
    } else {
      setAbsenceForm({ user_id: user.id, start_date: new Date(), end_date: new Date(), type: 'conges', reason: '', status: 'pending' });
    }
    setAbsenceDialog({ open: true, editing: abs });
  };
  const saveAbsence = async () => {
    try {
      const payload = { ...absenceForm, start_date: toNoonUTC(absenceForm.start_date), end_date: toNoonUTC(absenceForm.end_date) };
      if (absenceDialog.editing) { await absencesApi.update(absenceDialog.editing.id, payload); toast.success('Absence modifiee'); }
      else { await absencesApi.create(payload); toast.success('Absence creee'); }
      setAbsenceDialog({ open: false, editing: null }); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const deleteAbsence = async (id) => {
    if (!window.confirm('Supprimer cette absence ?')) return;
    try { await absencesApi.delete(id); toast.success('Supprime'); fetchData(); } catch { toast.error('Erreur'); }
  };

  // Pie chart data: time by project
  const COLORS = ['#2563eb', '#16a34a', '#ea580c', '#7c3aed', '#dc2626', '#0891b2', '#d97706', '#4f46e5', '#059669', '#e11d48'];
  const timeByProject = useMemo(() => {
    const map = {};
    periodEntries.forEach(e => {
      const p = projects.find(p => p.id === e.project_id);
      const name = p ? (p.code || p.name) : 'Autre';
      map[name] = (map[name] || 0) + e.time_spent;
    });
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    return Object.entries(map).map(([name, value]) => ({
      name, value: Math.round(value * 100) / 100,
      pct: total > 0 ? Math.round(value / total * 100) : 0
    })).sort((a, b) => b.value - a.value);
  }, [periodEntries, projects]);

  // Pie chart data: time by resource
  const timeByResource = useMemo(() => {
    const map = {};
    periodEntries.forEach(e => {
      const u = users.find(u => u.id === e.user_id);
      const name = u ? u.name : 'Inconnu';
      map[name] = (map[name] || 0) + e.time_spent;
    });
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    return Object.entries(map).map(([name, value]) => ({
      name, value: Math.round(value * 100) / 100,
      pct: total > 0 ? Math.round(value / total * 100) : 0
    })).sort((a, b) => b.value - a.value);
  }, [periodEntries, users]);

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="time-tracking-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-heading font-extrabold text-gradient">Suivi Temps & Absences</h1>
          <p className="text-slate-600 mt-1">Saisissez votre temps et gerez vos absences</p>
        </div>
        <div className="flex items-center gap-3">
          {isManager && (
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
              <Button variant={viewScope === 'my' ? 'default' : 'ghost'} size="sm" onClick={() => setViewScope('my')} data-testid="scope-my-btn">
                <User size={16} className="mr-1" />Mon temps
              </Button>
              <Button variant={viewScope === 'team' ? 'default' : 'ghost'} size="sm" onClick={() => setViewScope('team')} data-testid="scope-team-btn">
                <Users size={16} className="mr-1" />Equipe
              </Button>
            </div>
          )}
          {user?.is_external_provider && <div className="flex items-center gap-2">
            <div className="text-xs text-right hidden xl:block">
              <div className="font-semibold">CRA {format(new Date(),'MM/yyyy')}</div>
              <div className={craMonthStatus?.coverage?.complete?'text-green-600':'text-amber-600'}>{craMonthStatus?.coverage ? `${craMonthStatus.coverage.worked_days} J travail + ${craMonthStatus.coverage.absence_days} J absence / ${craMonthStatus.coverage.expected_days} J` : 'Chargement...'}</div>
            </div>
            <Button variant="outline" onClick={submitMyCra} disabled={!craMonthStatus?.coverage?.complete || ['submitted','manager_validated','closed'].includes(craMonthStatus?.status)} data-testid="submit-cra-month">
              <CheckCircle size={16} className="mr-1" />{craMonthStatus?.status==='closed'?'CRA cloture':craMonthStatus?.status==='manager_validated'?'CRA valide responsable':craMonthStatus?.status==='submitted'?'CRA soumis':'Valider mon CRA & soumettre'}
            </Button>
          </div>}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            {hasPermission('time:create') && <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-time-entry-btn">
                <Plus size={18} className="mr-2" />Saisir du temps
              </Button>
            </DialogTrigger>}
            <DialogContent className="dialog-wide">
              <DialogHeader>
                <DialogTitle>Nouvelle saisie de temps</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Projet *</Label>
                  <Select value={newEntry.project_id} onValueChange={v => setNewEntry(p => ({ ...p, project_id: v, task_id: '' }))}>
                    <SelectTrigger data-testid="time-project-select"><SelectValue placeholder="Selectionner un projet" /></SelectTrigger>
                    <SelectContent>
                      {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {newEntry.project_id && (
                  <div className="space-y-2">
                    <Label>Tache / sous-tache *</Label>
                    <Select value={newEntry.task_id} onValueChange={v => setNewEntry(p => ({ ...p, task_id: v }))}>
                      <SelectTrigger data-testid="time-task-select"><SelectValue placeholder="Associer a une tache" /></SelectTrigger>
                      <SelectContent>
                        {tasks.filter(t => t.project_id === newEntry.project_id).sort((a,b)=>Number(a.priority_level||0)-Number(b.priority_level||0)||Number(a.position||0)-Number(b.position||0)).map(t => { const parent=tasks.find(x=>x.id===t.parent_task_id); return <SelectItem key={t.id} value={t.id}>{parent ? `↳ ${parent.title} > ${t.title}` : `▰ ${t.title}`}</SelectItem>; })}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Activite *</Label>
                  <Select value={newEntry.activity} onValueChange={v => setNewEntry(p => ({ ...p, activity: v }))}>
                    <SelectTrigger data-testid="time-activity-select"><SelectValue placeholder="Type d'activite" /></SelectTrigger>
                    <SelectContent>
                      {ACTIVITIES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="time-date-btn">
                          <CalendarBlank size={16} className="mr-2" />
                          {format(newEntry.date, 'dd/MM/yyyy', { locale: fr })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={newEntry.date} onSelect={d => d && setNewEntry(p => ({ ...p, date: d }))} locale={fr} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Temps (jour) *</Label>
                    <Input type="number" step="0.01" min="0.01" max="1" value={newEntry.time_spent}
                      onChange={e => setNewEntry(p => ({ ...p, time_spent: e.target.value }))}
                      placeholder="0.25" data-testid="time-spent-input" />
                    <p className="text-xs text-slate-500">0.25 = 1/4 de jour</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={newEntry.description} onChange={e => setNewEntry(p => ({ ...p, description: e.target.value }))}
                    placeholder="Details de l'activite..." rows={2} data-testid="time-description-input" />
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <Switch
                    checked={newEntry.billable}
                    onCheckedChange={v => setNewEntry(p => ({ ...p, billable: v }))}
                    data-testid="time-billable-switch"
                    className="data-[state=checked]:bg-green-500"
                  />
                  <Label className="text-xs cursor-pointer">Facturable</Label>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Annuler</Button>
                  <Button className="btn-primary" onClick={handleCreateEntry}
                    disabled={!newEntry.project_id || !newEntry.task_id || !newEntry.activity || !newEntry.time_spent}
                    data-testid="submit-time-entry-btn">
                    Enregistrer
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-3">
          <div className="flex items-center gap-2 text-blue-800">
            <Info size={18} />
            <span className="text-sm">
              <strong>Conversion :</strong> 1 jour = 7h. 0.01j ~ 4min, 0.25j ~ 1h45, 0.50j ~ 3h30.
              <strong className="ml-2">Limite :</strong> 1.00 jour max par personne par jour (tous projets confondus).
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Filters & CRA Exports */}
      <div className="flex flex-wrap items-center gap-3">
        {isManager && (
          <Select value={resourceFilter} onValueChange={setResourceFilter}>
            <SelectTrigger className="w-48" data-testid="time-resource-filter">
              <SelectValue placeholder="Toutes les ressources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les ressources</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={projectFilter} onValueChange={v => { setProjectFilter(v); setTaskFilter('all'); }}>
          <SelectTrigger className="w-48" data-testid="time-project-filter">
            <SelectValue placeholder="Tous les projets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les projets</SelectItem>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={taskFilter} onValueChange={setTaskFilter}>
          <SelectTrigger className="w-48" data-testid="time-task-filter">
            <SelectValue placeholder="Toutes les taches" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les taches</SelectItem>
            {tasks.filter(t => projectFilter === 'all' || t.project_id === projectFilter).map(t => (
              <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          {googleStatus.enabled && <Button variant="outline" size="sm" className="text-xs" onClick={syncGoogleCalendar} disabled={googleSyncing || !googleStatus.configured} title={googleStatus.linked ? 'Importer les reunions Google du projet dans les temps' : 'Rattachez Google Agenda depuis Mon profil'}>
            <CalendarBlank size={14} className="mr-1 text-blue-600" />{googleSyncing ? 'Synchronisation...' : googleStatus.linked ? 'Sync Google Agenda' : 'Google Agenda'}
          </Button>}
          <Button variant="outline" size="sm" className="text-xs" onClick={() => {
            const params = {};
            if (resourceFilter !== 'all') params.user_id = resourceFilter;
            params.start_date = format(periodStart, 'yyyy-MM-dd');
            params.end_date = format(periodEnd, 'yyyy-MM-dd');
            window.open(timeApi.exportCRAExcel(params), '_blank');
          }} data-testid="export-cra-excel-btn">
            <Download size={14} className="mr-1 text-green-600" />CRA Excel
          </Button>
          <Button variant="outline" size="sm" className="text-xs" onClick={() => {
            const doc = new jsPDF('l', 'mm', 'a4');
            doc.setFontSize(14);
            doc.text('Compte Rendu d\'Activite (CRA)', 14, 16);
            doc.setFontSize(8);
            const periodLabel = viewPeriod === 'week'
              ? `Semaine du ${format(periodStart, 'dd/MM/yyyy')} au ${format(periodEnd, 'dd/MM/yyyy')}`
              : format(periodStart, 'MMMM yyyy', { locale: fr });
            doc.text(`Periode: ${periodLabel}`, 14, 22);
            if (resourceFilter !== 'all') {
              const u = users.find(x => x.id === resourceFilter);
              doc.text(`Ressource: R-${String(u?.id || resourceFilter).slice(-6).toUpperCase()}`, 14, 27);
            }
            const rows = periodEntries.map(e => {
              const proj = projects.find(p => p.id === e.project_id);
              const usr = users.find(u => u.id === e.user_id);
              return [
                format(new Date(e.date), 'dd/MM/yyyy'),
                usr?.id ? `R-${String(usr.id).slice(-6).toUpperCase()}` : '-',
                proj?.code || '-',
                e.activity || '-',
                formatTimeHundredths(e.time_spent),
                e.billable !== false ? 'Oui' : 'Non',
                e.description || ''
              ];
            });
            const tableResult = autoTable(doc, { startY: 32, head: [['Date', 'Ressource', 'Projet', 'Activite', 'Temps', 'Fact.', 'Description']], body: rows, styles: { fontSize: 7 }, headStyles: { fillColor: [30, 41, 59] } });
            const totalBillable = periodEntries.filter(e => e.billable !== false).reduce((s, e) => s + e.time_spent, 0);
            const totalNonBillable = periodEntries.filter(e => e.billable === false).reduce((s, e) => s + e.time_spent, 0);
            const finalY = (tableResult?.finalY || doc.internal.pageSize.height - 30) + 8;
            doc.setFontSize(9);
            doc.text(`Total Facturable: ${formatTimeHundredths(totalBillable)}  |  Total Non-Facturable: ${formatTimeHundredths(totalNonBillable)}  |  Total: ${formatTimeHundredths(totalBillable + totalNonBillable)}`, 14, finalY);
            doc.save('cra.pdf');
            toast.success('CRA PDF genere');
          }} data-testid="export-cra-pdf-btn">
            <FilePdf size={14} className="mr-1 text-red-500" />CRA PDF
          </Button>
        </div>
      </div>

      {/* Period Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigatePeriod('prev')} data-testid="period-prev-btn">
            <ArrowLeft size={16} />
          </Button>
          <div className="text-center min-w-[240px]">
            <p className="font-heading font-bold text-lg text-slate-900">
              {viewPeriod === 'week'
                ? `Semaine du ${format(periodStart, 'dd MMM', { locale: fr })} au ${format(periodEnd, 'dd MMM yyyy', { locale: fr })}`
                : format(periodStart, 'MMMM yyyy', { locale: fr })
              }
            </p>
          </div>
          <Button variant="outline" size="icon" onClick={() => navigatePeriod('next')} data-testid="period-next-btn">
            <ArrowRight size={16} />
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
            <Button variant={viewPeriod === 'week' ? 'default' : 'ghost'} size="sm" onClick={() => setViewPeriod('week')} data-testid="period-week-btn">
              Semaine
            </Button>
            <Button variant={viewPeriod === 'month' ? 'default' : 'ghost'} size="sm" onClick={() => setViewPeriod('month')} data-testid="period-month-btn">
              Mois
            </Button>
          </div>
          <Badge className="bg-blue-100 text-blue-800 text-base px-3 py-1">
            <Clock size={16} className="mr-1" />{formatTimeHundredths(periodTotal)}
          </Badge>
        </div>
      </div>

      {/* Week Grid (shown in week mode) */}
      {viewPeriod === 'week' && (
        <Card className="card-surface" data-testid="week-overview">
          <CardContent className="pt-4">
            <div className="grid grid-cols-7 gap-2">
              {periodDays.map(day => {
                const dayTotal = getDayTotal(day, viewScope === 'my' ? user?.id : null);
                const isSelected = isSameDay(day, selectedDate);
                const isToday = isSameDay(day, new Date());
                const progress = Math.min((dayTotal / 1) * 100, 100);
                return (
                  <button key={day.toISOString()} onClick={() => setSelectedDate(day)}
                    className={`p-3 rounded-lg border transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'} ${isToday ? 'ring-2 ring-blue-200' : ''}`}
                    data-testid={`day-${format(day, 'yyyy-MM-dd')}`}>
                    <p className="text-xs text-slate-500 capitalize">{format(day, 'EEE', { locale: fr })}</p>
                    <p className={`text-lg font-bold ${isSelected ? 'text-blue-600' : 'text-slate-900'}`}>{format(day, 'd')}</p>
                    <Progress value={progress} className="h-1 mt-2" />
                    <p className="text-xs text-slate-500 mt-1">{formatTimeHundredths(dayTotal)}</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Month Grid (shown in month mode) */}
      {viewPeriod === 'month' && (
        <Card className="card-surface" data-testid="month-overview">
          <CardContent className="pt-4">
            <div className="grid grid-cols-7 gap-1">
              {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                <div key={d} className="text-center text-xs font-semibold text-slate-500 py-1">{d}</div>
              ))}
              {(() => {
                const firstDayOfMonth = periodStart;
                const startDay = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
                const endDay = endOfWeek(periodEnd, { weekStartsOn: 1 });
                const days = eachDayOfInterval({ start: startDay, end: endDay });
                return days.map(day => {
                  const dayTotal = getDayTotal(day, viewScope === 'my' ? user?.id : null);
                  const isSelected = isSameDay(day, selectedDate);
                  const isCurrentMonth = day.getMonth() === periodStart.getMonth();
                  const isToday = isSameDay(day, new Date());
                  return (
                    <button key={day.toISOString()} onClick={() => setSelectedDate(day)}
                      className={`p-1.5 rounded text-center transition-all ${isSelected ? 'bg-blue-500 text-white' : isToday ? 'bg-blue-50 border border-blue-200' : ''} ${!isCurrentMonth ? 'opacity-30' : ''}`}>
                      <p className="text-xs font-medium">{format(day, 'd')}</p>
                      {dayTotal > 0 && (
                        <p className={`text-[9px] font-mono ${isSelected ? 'text-blue-100' : 'text-blue-600'}`}>
                          {dayTotal.toFixed(2)}
                        </p>
                      )}
                    </button>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team View: entries grouped by user */}
      {viewScope === 'team' && isManager && Object.keys(entriesByUser).length > 0 && (
        <Card className="card-surface" data-testid="team-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Users size={20} className="text-blue-600" />
              Recapitulatif equipe - {viewPeriod === 'week' ? 'Semaine' : 'Mois'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Membre</TableHead>
                  <TableHead className="font-semibold">Nb saisies</TableHead>
                  <TableHead className="font-semibold">Total</TableHead>
                  <TableHead className="font-semibold">Projets</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(entriesByUser).map(([uid, data]) => {
                  const total = data.entries.reduce((s, e) => s + e.time_spent, 0);
                  const projectCodes = [...new Set(data.entries.map(e => e.project_code || '?'))];
                  return (
                    <TableRow key={uid} data-testid={`team-user-${uid}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="w-7 h-7">
                            <AvatarFallback className="text-[9px] bg-blue-100 text-blue-700">{getInitials(data.name)}</AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{data.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>{data.entries.length}</TableCell>
                      <TableCell>
                        <span className="font-mono font-bold text-blue-600">{formatTimeHundredths(total)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {projectCodes.slice(0, 3).map((c, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                          ))}
                          {projectCodes.length > 3 && <span className="text-xs text-slate-400">+{projectCodes.length - 3}</span>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Day Details */}
      <Card className="card-surface" data-testid="day-details">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center justify-between">
            <span>{format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}</span>
            <span className="text-blue-600 text-base font-mono">
              {formatTimeHundredths(getDayTotal(selectedDate, viewScope === 'my' ? user?.id : null))} / 1.00 j
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse space-y-3">
              {[1,2,3].map(i => <div key={i} className="h-16 bg-slate-200 rounded" />)}
            </div>
          ) : selectedDateEntries.length === 0 ? (
            <div className="py-8 text-center">
              <Clock size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Aucune saisie pour cette journee</p>
              <Button variant="outline" className="mt-4" onClick={() => { setNewEntry(p => ({ ...p, date: selectedDate })); setIsCreateDialogOpen(true); }}>
                <Plus size={16} className="mr-2" />Ajouter une saisie
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {viewScope === 'team' && <TableHead className="font-semibold">Membre</TableHead>}
                  <TableHead className="font-semibold">Projet</TableHead>
                  <TableHead className="font-semibold">Activite</TableHead>
                  <TableHead className="font-semibold">Temps</TableHead>
                  <TableHead className="font-semibold">Fact.</TableHead>
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedDateEntries.map(entry => {
                  const project = projects.find(p => p.id === entry.project_id);
                  const task = tasks.find(t => t.id === entry.task_id);
                  return (
                    <TableRow key={entry.id} data-testid={`time-entry-${entry.id}`}>
                      {viewScope === 'team' && (
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Avatar className="w-5 h-5"><AvatarFallback className="text-[7px] bg-blue-100 text-blue-700">{getInitials(entry.user_name || '')}</AvatarFallback></Avatar>
                            <span className="text-xs">{entry.user_name || '-'}</span>
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        <span className="font-mono text-sm">{project?.code || entry.project_code || '-'}</span>
                        {(task || entry.task_name) && (
                          <p className="text-xs text-blue-600 flex items-center gap-1 mt-0.5">
                            <Target size={10} />{task?.title || entry.task_name}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>{entry.activity}</TableCell>
                      <TableCell>
                        <span className="font-mono font-medium">{formatTimeHundredths(entry.time_spent)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={entry.billable !== false ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-slate-100 text-slate-500 text-[10px]'} data-testid={`billable-badge-${entry.id}`}>
                          {entry.billable !== false ? 'Oui' : 'Non'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600 line-clamp-1">{entry.description || '-'}</span>
                      </TableCell>
                      <TableCell>
                        {entry.validated ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle size={14} />Valide
                          </span>
                        ) : (
                          <span className="text-slate-500 text-sm">En attente</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {!entry.validated && (
                            <>
                              {hasPermission('time:update') && <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600"
                                onClick={() => setEditingEntry({ ...entry })} data-testid={`edit-time-${entry.id}`}>
                                <PencilSimple size={16} />
                              </Button>}
                              {isManager && (
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600"
                                  onClick={() => handleValidateEntry(entry.id)}>
                                  <CheckCircle size={16} />
                                </Button>
                              )}
                              {hasPermission('time:delete') && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600"
                                onClick={() => handleDeleteEntry(entry.id)}>
                                <Trash size={16} />
                              </Button>}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Time Entry Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier la saisie</DialogTitle>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Activite</Label>
                <Select value={editingEntry.activity || ''} onValueChange={v => setEditingEntry(p => ({ ...p, activity: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIVITIES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Temps (jour)</Label>
                <Input type="number" step="0.01" min="0.01" max="1" value={editingEntry.time_spent}
                  onChange={e => setEditingEntry(p => ({ ...p, time_spent: e.target.value }))} data-testid="edit-time-spent-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={editingEntry.description || ''} onChange={e => setEditingEntry(p => ({ ...p, description: e.target.value }))}
                  placeholder="Description..." rows={2} data-testid="edit-time-description-input" />
              </div>
              <div className="flex items-center gap-3 pt-1">
                <Switch
                  checked={editingEntry.billable !== false}
                  onCheckedChange={v => setEditingEntry(p => ({ ...p, billable: v }))}
                  data-testid="edit-time-billable-switch"
                  className="data-[state=checked]:bg-green-500"
                />
                <Label className="text-xs cursor-pointer">Facturable</Label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditingEntry(null)}>Annuler</Button>
                <Button className="btn-primary" onClick={handleUpdateEntry} data-testid="save-edit-time-btn">
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PIE CHARTS: Time by Project & by Resource */}
      {(timeByProject.length > 0 || timeByResource.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* By Project */}
          <Card className="card-surface" data-testid="pie-by-project">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm flex items-center gap-2"><Target size={16} className="text-blue-600" />Temps par projet</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 flex items-center justify-center">
              {timeByProject.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={timeByProject} cx="50%" cy="50%" outerRadius={100} innerRadius={45} dataKey="value" nameKey="name" label={({ name, pct }) => `${name} ${pct}%`} labelLine={{ strokeWidth: 1 }} style={{ fontSize: 11 }}>
                      {timeByProject.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v}j`, 'Temps']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-500 py-8">Aucune donnee</p>}
            </CardContent>
          </Card>

          {/* By Resource */}
          <Card className="card-surface" data-testid="pie-by-resource">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm flex items-center gap-2"><Users size={16} className="text-green-600" />Temps par ressource</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 flex items-center justify-center">
              {timeByResource.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={timeByResource} cx="50%" cy="50%" outerRadius={100} innerRadius={45} dataKey="value" nameKey="name" label={({ name, pct }) => `${name} ${pct}%`} labelLine={{ strokeWidth: 1 }} style={{ fontSize: 11 }}>
                      {timeByResource.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v}j`, 'Temps']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-500 py-8">Aucune donnee</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {/* PROJECTS PROGRESS CHART */}
      {projectsProgress.length > 0 && (
        <Card className="card-surface" data-testid="projects-progress-chart">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2"><Target size={16} className="text-blue-600" />Avancement des projets</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={Math.max(200, projectsProgress.length * 40)}>
              <BarChart data={projectsProgress} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <YAxis type="category" dataKey="code" width={80} tick={{ fontSize: 10 }} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="bg-white border rounded-lg p-3 shadow-lg text-xs">
                      <p className="font-semibold">{d.code} - {d.name}</p>
                      <p>Avancement: <strong>{d.progress}%</strong></p>
                      <p>Taches: {d.closed_tasks}/{d.total_tasks} terminees, {d.in_progress} en cours</p>
                      <p>Temps: {d.time_spent}j / {d.estimated_days || '?'}j estimes</p>
                    </div>
                  );
                }} />
                <Bar dataKey="progress" radius={[0, 4, 4, 0]} barSize={20}>
                  {projectsProgress.map((p, i) => (
                    <Cell key={i} fill={p.progress >= 80 ? '#16a34a' : p.progress >= 50 ? '#2563eb' : p.progress >= 25 ? '#ea580c' : '#dc2626'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* ABSENCES SECTION */}
      <Card className="card-surface" data-testid="absences-section">
        <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2"><CalendarX size={16} className="text-orange-500" />Absences ({absences.length})</CardTitle>
          <Button size="sm" onClick={() => openAbsenceDialog()} data-testid="add-absence-btn"><Plus size={14} className="mr-1" />Nouvelle absence</Button>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full pro-table">
            <thead><tr className="border-b bg-slate-50">
              <th className="px-4 py-2 text-left text-xs">Collaborateur</th>
              <th className="px-4 py-2 text-left text-xs">Type</th>
              <th className="px-4 py-2 text-left text-xs">Du</th>
              <th className="px-4 py-2 text-left text-xs">Au</th>
              <th className="px-4 py-2 text-left text-xs">Motif</th>
              <th className="px-4 py-2 text-left text-xs">Statut</th>
              <th className="px-4 py-2 text-center text-xs w-20">Actions</th>
            </tr></thead>
            <tbody>
              {absences.map(abs => {
                const u = users.find(u => u.id === abs.user_id);
                const typeLabel = ABSENCE_TYPES.find(t => t.value === abs.type)?.label || abs.type;
                const startD = abs.start_date ? new Date(abs.start_date) : null;
                const endD = abs.end_date ? new Date(abs.end_date) : null;
                return (
                  <tr key={abs.id} className="border-b">
                    <td className="px-4 py-2 text-sm">{u?.name || abs.user_id}</td>
                    <td className="px-4 py-2"><Badge variant="outline" className="text-[10px]">{typeLabel}</Badge></td>
                    <td className="px-4 py-2 text-xs">{startD ? `${String(startD.getUTCDate()).padStart(2,'0')}/${String(startD.getUTCMonth()+1).padStart(2,'0')}/${startD.getUTCFullYear()}` : '-'}</td>
                    <td className="px-4 py-2 text-xs">{endD ? `${String(endD.getUTCDate()).padStart(2,'0')}/${String(endD.getUTCMonth()+1).padStart(2,'0')}/${endD.getUTCFullYear()}` : '-'}</td>
                    <td className="px-4 py-2 text-xs text-slate-600 max-w-[150px] truncate">{abs.reason || '-'}</td>
                    <td className="px-4 py-2"><Badge className={abs.status === 'approved' ? 'bg-green-100 text-green-700' : abs.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'} >{abs.status === 'approved' ? 'Approuve' : abs.status === 'rejected' ? 'Refuse' : 'En attente'}</Badge></td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openAbsenceDialog(abs)}><PencilSimple size={12} /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteAbsence(abs.id)}><Trash size={12} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {absences.length === 0 && <tr><td colSpan="7" className="px-4 py-6 text-center text-sm text-slate-500">Aucune absence</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Absence Dialog */}
      <Dialog open={absenceDialog.open} onOpenChange={(o) => setAbsenceDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{absenceDialog.editing ? 'Modifier' : 'Nouvelle'} absence</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {isManager && (
              <div className="space-y-1">
                <Label className="text-xs">Collaborateur</Label>
                <Select value={absenceForm.user_id} onValueChange={v => setAbsenceForm(p => ({ ...p, user_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <Select value={absenceForm.type} onValueChange={v => setAbsenceForm(p => ({ ...p, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ABSENCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Du</Label>
                <Popover><PopoverTrigger asChild>
                  <Button variant="outline" className="w-full text-xs h-9 justify-start"><CalendarBlank size={12} className="mr-1" />{format(absenceForm.start_date, 'dd/MM/yyyy')}</Button>
                </PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={absenceForm.start_date} onSelect={d => d && setAbsenceForm(p => ({ ...p, start_date: d }))} locale={fr} /></PopoverContent></Popover>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Au</Label>
                <Popover><PopoverTrigger asChild>
                  <Button variant="outline" className="w-full text-xs h-9 justify-start"><CalendarBlank size={12} className="mr-1" />{format(absenceForm.end_date, 'dd/MM/yyyy')}</Button>
                </PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={absenceForm.end_date} onSelect={d => d && setAbsenceForm(p => ({ ...p, end_date: d }))} locale={fr} /></PopoverContent></Popover>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Motif</Label>
              <Input value={absenceForm.reason} onChange={e => setAbsenceForm(p => ({ ...p, reason: e.target.value }))} placeholder="Motif (optionnel)" />
            </div>
            {isManager && absenceDialog.editing && (
              <div className="space-y-1">
                <Label className="text-xs">Statut</Label>
                <Select value={absenceForm.status} onValueChange={v => setAbsenceForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="approved">Approuve</SelectItem>
                    <SelectItem value="rejected">Refuse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setAbsenceDialog({ open: false, editing: null })}>Annuler</Button>
              <Button size="sm" onClick={saveAbsence} data-testid="save-absence-btn">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
